// app/javascript/widget/poller.ts — цикл опроса ленты.
//
// Виджет узнаёт о новых репликах поллингом (ActionCable — v-next), и цикл
// вокруг запроса нетривиален: у него свой каданс (ждём ответ — быстро,
// открытый чат на активной вкладке — обычный, свёрнутый или фоновая вкладка —
// медленно), защита от наложения запросов и правило «пустой виджет на чужой
// странице публичный API не трогает» (§12).
//
// Раньше это жило четырьмя методами в трёхтысячном классе виджета вперемешку с
// разбором ответа. Здесь — только цикл: сходить, когда пора, и не сходить,
// когда не надо. Что делать с ответом, знает хозяин (tick).
import type { Timers, TimerHandle } from './timers.ts';

export interface PollerHost {
  /** Есть ли смысл опрашивать: пустой свёрнутый виджет на чужой странице —
   *  нет (иначе каждая страница сайта грузила бы публичный API). */
  active(): boolean;
  /** Пауза до следующего опроса — каданс хозяина. */
  delay(): number;
  /** Один опрос: сходить за лентой и отрисовать. Исключения хозяин ловит сам. */
  tick(): Promise<void>;
}

export class Poller {
  private handle: TimerHandle | null = null;
  private running = false;
  // Поля объявлены явно: Node запускает тесты со strip-types, а сокращённая
  // запись через параметры конструктора там не поддерживается.
  private readonly host: PollerHost;
  private readonly timers: Timers;

  constructor(host: PollerHost, timers: Timers) {
    this.host = host;
    this.timers = timers;
  }

  /** Запустить цикл: первый опрос — сразу, дальше по кадансу. */
  start(): void {
    this.stop();
    void this.run();
  }

  /** Погасить цикл: заведённый таймер снимается, новый не появится. */
  stop(): void {
    this.timers.clear(this.handle);
    this.handle = null;
  }

  /** Внеочередной опрос (кнопка «Повторить», отправка реплики) — мимо каданса. */
  now(): void {
    void this.run();
  }

  /** Следующий опрос через заданную паузу; неактивный виджет не планируем. */
  schedule(delay: number): void {
    if (!this.host.active()) return;

    this.timers.clear(this.handle);
    // Выключенный виджет (блокировка §12) таймера не получит — after() отдаст null.
    this.handle = this.timers.after(delay, () => void this.run());
  }

  /** Идёт ли опрос прямо сейчас — наложения не допускаем. */
  get busy(): boolean {
    return this.running;
  }

  private async run(): Promise<void> {
    if (this.running) return;

    this.running = true;
    try {
      await this.host.tick();
    } finally {
      this.running = false;
    }
    this.schedule(this.host.delay());
  }
}
